// COP — client-side KAS rewrap (CONTRACT:I5-KEY-ACCESS.1.0), pure @opentdf/sdk.
//
// This is the ONLY place the COP touches real cryptography, and it does it the way
// TDFLite's own demoweb does: the BROWSER rewraps directly against the live KAS, AS
// the logged-in persona (the session JWT is the rewrap subject). No Go proxy, no
// hardcoded operators, no second identity. Same-origin through the caddy gateway
// (page origin + /kas) so there is no mixed-content; the SDK derives the platform
// URL from that. The token is read live through the interceptor, so a persona switch
// (setKasToken) takes effect on the next open. The KAS is authoritative on release;
// the local PDP (I4) only filters visibility and never produces a key.

import { OpenTDF, authTokenInterceptor } from "@opentdf/sdk";
import { deriveBoundFqns, type SecurityView } from "../../../../packages/abac-pdp/src/fqn.ts";
import { validateMarkings, type PolicyBundle } from "../../../../packages/abac-pdp/src/index.ts";
import { CLASS_ORDER } from "../../../../packages/abac-pdp/src/index.ts";

/** Class rank for the seal-floor assertion = the ONE shared canonical CLASS_ORDER (M3
 *  fix). A body whose classification > UNCLASSIFIED MUST bind the hierarchy facet or we
 *  refuse to seal (an unbound-above-UNCLASSIFIED TDF would release to any reader). */
const CLASS_RANK = CLASS_ORDER;

/** Releasability tokens that DO NOT narrow the audience (a broadcast/everyone marking).
 *  Any other non-empty releasability token is treated as a RESTRICTION (it limits who
 *  may read), which arms the mandatory-namespace guard (R3). Conservative + fail-closed:
 *  a coalition tag like NATO/FVEY/USA narrows the audience, so it counts as restrictive. */
const PERMISSIVE_REL = new Set(["ALL", "PUBLIC", "UNRESTRICTED", "EVERYONE", "*"]);

/** Is this marking RESTRICTIVE — i.e. above-UNCLASSIFIED, caveated, or audience-narrowed?
 *  A restrictive marking MUST be sealed against a verified-live bundle namespace (R3); an
 *  unrestricted UNCLASSIFIED / release-to-everyone body may seal without one. */
function isRestrictiveMarking(labels: ProtectLabels): boolean {
  const cls = (labels.classification ?? "UNCLASSIFIED").toUpperCase();
  const clsRank = CLASS_RANK[cls] ?? Number.POSITIVE_INFINITY; // unknown class → treat as restrictive
  if (clsRank > CLASS_RANK.UNCLASSIFIED) return true;
  if ((labels.caveats ?? []).filter(Boolean).length > 0) return true;
  // A non-empty releasability that contains any audience-narrowing token is a restriction.
  const rel = (labels.releasability ?? []).filter(Boolean);
  if (rel.some((r) => !PERMISSIVE_REL.has(r.toUpperCase()))) return true;
  return false;
}

/** The marking labels the COP binds into a SEALED chat body (the produce side of I5).
 *  Subset of CONTRACT:I2 — mirrors the gateway's ProtectLabels (protect.ts:22) so the
 *  browser seal binds the IDENTICAL attribute shape the gateway would. */
export interface ProtectLabels {
  classification?: string;
  releasability?: string[];
  mission?: string[];
  caveats?: string[];
}

/** The marking the DECRYPTED inner payload self-declares. Encrypted-CoT (P4) model:
 *  the wire/outer marking is the releasable floor; the inner can be HIGHER. A cleared
 *  reader must see — and the COP must MARK the screen to — this higher classification. */
export interface InnerMarking {
  classification: string;
  caveats?: string[];
  releasability?: string[];
  mission?: string[];
}

/** The outcome of a real KAS rewrap on a selected protected contact. */
export interface RewrapOutcome {
  granted: boolean;
  plaintext?: string; // REAL decrypted text, only on grant
  marking?: InnerMarking; // the decrypted inner marking (drives banner elevation), only on grant
  reason?: string; // I5 code on deny (e.g. ACCESS_DENIED)
}

/** A seeded, genuinely-TDF-protected object (from public/tdf/index.json) — carries
 *  both its markings (for the PDP filter) and its .tdf bytes URL (for the KAS open),
 *  so it flows through the SAME I4-filter + I5-release path as every other object. */
export interface SeededObject {
  id: string;
  name: string;
  affiliation: string;
  coords: [number, number];
  tdf: string; // URL to the .tdf bytes (same-origin)
  bound_fqns: string[];
  security: {
    classification: string;
    releasability: string[];
    mission: string[];
    caveats: string[];
  };
}

let token = "";
let client: OpenTDF | null = null;

// --- SILENT TOKEN REFRESH (the "key withheld after 5 minutes" fix) --------------------------
// The idplite access token lives ~5 min; the SDK's authTokenInterceptor is a dumb per-request
// bearer stamper, so a static token getter (what this module used to pass) would keep stamping
// an EXPIRED token → KAS 401 → decryptTdf fails closed → the UI renders a FAKE "key withheld"
// that is indistinguishable from a real clearance denial. We fix it the way the SDK's own token
// providers do — proactively refresh ~30s BEFORE expiry, dedup concurrent refreshes — but route
// the refresh through a host callback that PERSISTS the rotated refresh token to sessionStorage
// (the stock refreshTokenProvider rotates in memory only, so a page reload would break; ours
// survives reload). The refresh mints a fresh access token from the 7-day refresh token — no
// password re-prompt. On any refresh failure we return the (stale) token unchanged and fall
// closed exactly as before: a genuine re-login is then required. Never weakens the KAS gate.
const TOKEN_REFRESH_SKEW_MS = 30_000; // refresh this long before exp (matches the SDK's 30s buffer)
let tokenExpiresAt = 0; // ms since epoch; 0 = unknown (never proactively refresh)
let tokenGeneration = 0; // bumped on every identity switch — guards against a cross-persona overwrite
let refresher: (() => Promise<string | null>) | null = null;
let refreshInFlight: Promise<string | null> | null = null;

// ── HMR SURVIVAL (dev-only; the 2026-07-05 dev-tab bite). A Vite hot update that re-evaluates
// this module resets the module-held refresher to null, and the interceptor's documented
// fallback is then PRE-FIX behavior: stamp the (eventually expired) token → KAS 401 → a
// DENY-looking failure in a long-lived dev tab. Two defenses, both dev-only + guarded so the
// prod build and headless tsx are untouched:
//   1. carry the live refresher across hot updates via import.meta.hot.data;
//   2. NEVER let the failure be silent again — a one-shot loud warn when we are about to stamp
//      an EXPIRED token with NO refresher registered (our own copy of the upstream footgun warn
//      we PR'd to the OpenTDF SDK — dogfooding it here).
try {
  const hot = (import.meta as unknown as { hot?: { data: Record<string, unknown> } }).hot;
  const carried = hot?.data?.kasTokenRefresher;
  if (typeof carried === "function") refresher = carried as () => Promise<string | null>;
} catch {
  /* non-Vite context (tsx/tests) — no HMR to survive */
}
let warnedExpiredNoRefresher = false;
function warnIfStampingExpiredWithoutRefresher(): void {
  if (warnedExpiredNoRefresher || refresher) return;
  if (tokenExpiresAt > 0 && Date.now() >= tokenExpiresAt) {
    warnedExpiredNoRefresher = true;
    console.warn(
      "[kas] stamping an EXPIRED access token with NO refresher registered — KAS calls will 401 " +
        "and read as denials. In a dev tab this is usually an HMR update that dropped the " +
        "refresher (hard-reload the tab); at boot it means setTokenRefresher was never wired.",
    );
  }
}

/** Decode a JWT's `exp` claim to epoch-ms (0 if unparseable). No verification — the KAS is
 *  authoritative; this only schedules the proactive refresh. */
function jwtExpiryMs(jwt: string): number {
  try {
    const [, payload] = jwt.split(".");
    const claims = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/"))) as { exp?: number };
    return typeof claims.exp === "number" ? claims.exp * 1000 : 0;
  } catch {
    return 0;
  }
}

// --- SESSION-STATE SURFACE (the "silent sealed rows" fix) -----------------------------------
// The chat panel (and any other surface) can now render an HONEST session state instead of
// letting an expired token masquerade as clearance denials, and can re-decode sealed rows the
// moment a fresh token lands (silent refresh OR an operator re-login via setKasToken).

/** The current access-token state, for HONEST UI: "none" (logged out / not initialized),
 *  "expired" (past exp — KAS calls will 401 and read as denials), "expiring" (within the
 *  proactive-refresh skew), "ok". Federated sessions have NO refresh token (idplite policy:
 *  re-auth goes back through Google), so "expired" there means a re-login is required. */
export type AuthTokenState = "none" | "ok" | "expiring" | "expired";
export function tokenState(): AuthTokenState {
  if (!token) return "none";
  if (tokenExpiresAt <= 0) return "ok"; // unknown exp — never claim expired
  const now = Date.now();
  if (now >= tokenExpiresAt) return "expired";
  if (now >= tokenExpiresAt - TOKEN_REFRESH_SKEW_MS) return "expiring";
  return "ok";
}

/** Can this session silently renew (a refresher is wired — persona/password sessions)?
 *  False for federated sessions (no refresh token by idplite design) — the UI should then
 *  direct the operator to re-authenticate rather than imply auto-recovery. */
export function canSilentlyRenew(): boolean {
  return refresher !== null;
}

/** Listeners fired when a FRESH token is ADOPTED by the silent refresh (same persona). A
 *  persona SWITCH does NOT fire these (setKasToken drives the full identity-switch rebuild,
 *  which re-decodes everything anyway) — these exist so same-persona recovery can re-decode
 *  rows that were sealed ONLY by the expired token. */
const tokenRenewListeners = new Set<() => void>();
export function onTokenRenewed(fn: () => void): () => void {
  tokenRenewListeners.add(fn);
  return () => tokenRenewListeners.delete(fn);
}
function fireTokenRenewed(): void {
  for (const fn of tokenRenewListeners) {
    try {
      fn();
    } catch (e) {
      console.error("[kas] token-renewed listener threw (continuing):", e);
    }
  }
}

/** Register the host's silent-refresh callback. It must mint a fresh access token (persisting
 *  the rotated refresh token) and return it, or null on failure. Wired once at boot; cleared on
 *  teardown. Without it, the interceptor simply returns the current token (pre-fix behavior). */
export function setTokenRefresher(fn: (() => Promise<string | null>) | null): void {
  refresher = fn;
  warnedExpiredNoRefresher = false; // a (re)wire re-arms the one-shot diagnostic
  try {
    const hot = (import.meta as unknown as { hot?: { data: Record<string, unknown> } }).hot;
    if (hot) hot.data.kasTokenRefresher = fn ?? undefined; // survive the next hot update
  } catch {
    /* non-Vite context */
  }
}

/** Ensure `token` is fresh before it is stamped on a KAS request. Proactive: refreshes when the
 *  token is within TOKEN_REFRESH_SKEW_MS of expiry (or `force`, used by the reactive 401 retry).
 *  Serialized via refreshInFlight so N concurrent rewrap calls share ONE refresh. A generation
 *  guard drops the result if a persona switch happened mid-refresh (never stamp persona A's
 *  refreshed token after a switch to persona B). */
async function ensureFreshToken(force: boolean): Promise<void> {
  if (!refresher) {
    warnIfStampingExpiredWithoutRefresher();
    return;
  }
  const near = tokenExpiresAt > 0 && Date.now() >= tokenExpiresAt - TOKEN_REFRESH_SKEW_MS;
  if (!force && !near) return;
  const gen = tokenGeneration;
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        return refresher ? await refresher() : null;
      } finally {
        refreshInFlight = null;
      }
    })();
  }
  const fresh = await refreshInFlight;
  // Only adopt the refreshed token if NO identity switch intervened (setKasToken bumps the
  // generation). A stale cross-persona token must never land on the live client.
  if (fresh && gen === tokenGeneration) {
    token = fresh;
    tokenExpiresAt = jwtExpiryMs(fresh);
    // SAME-PERSONA RECOVERY: a fresh token just landed — let subscribers (the chat panel's
    // retryRedacted) re-decode rows that were sealed only by the expired token.
    fireTokenRenewed();
  }
}

/** The current (refreshed-if-needed) access token for a NON-KAS same-origin call that must
 *  authenticate as the logged-in persona (today: the /emit egress bearer stamp). Reuses the
 *  exact interceptor path (ensureFreshToken + the module token) — zero new token state, same
 *  proactive refresh, same generation guard. `force` mirrors the reactive-401 retry in
 *  decryptTdf: a caller that just saw a 401 forces ONE refresh before its single retry.
 *  Returns "" when logged out / never initialized (callers stamp no header then). */
export async function freshAccessToken(force = false): Promise<string> {
  await ensureFreshToken(force);
  return token;
}

// --- KAS REACHABILITY HEARTBEAT (Phase 2 Step E) ---------------------------------------
// A lightweight periodic probe of the live KAS so the COP FAILS CLOSED HONESTLY when the KAS
// is unreachable: protected content stays SEALED at its OUTER marking (never plaintext) and a
// "KAS OFFLINE — decrypt unavailable" banner appears, instead of firing a rewrap attempt (and
// an error) per selected item.
//
// The probe GETs the KAS public-key endpoint (/kas/v2/kas_public_key) — an unauthenticated,
// same-origin read that returns 200 whenever the KAS is up (verified 200-from-the-box in the
// deploy runbook). It is NOT a clearance gate: it never releases a key and never carries the
// token; it only answers "is the KAS answering at all?". The KAS remains authoritative on
// release — this flag only decides whether we bother ASKING.
//
// THRESHOLDED (hysteresis — no flapping): the flag flips to OFFLINE only after
// KAS_OFFLINE_THRESHOLD consecutive FAILED probes, and flips back to ONLINE on the FIRST
// successful probe. At KAS_HEARTBEAT_MS cadence that is a ~30s worst-case detection window and
// a ~10s recovery — slow enough to ride out one transient blip, fast enough to stay honest.
const KAS_HEARTBEAT_MS = 10_000;
const KAS_OFFLINE_THRESHOLD = 3;
const KAS_PROBE_TIMEOUT_MS = 4_000;

let kasOffline = false;
let kasProbeFailures = 0;

/** Synchronous read of the current KAS reachability verdict. decryptTdf consults this to
 *  SKIP a rewrap attempt (fail closed) when the KAS is known-unreachable. Default false
 *  (assume reachable) — the first probe fires immediately at heartbeat start to correct it,
 *  and decryptTdf itself still fails closed on any per-call error even before the flag flips. */
export function isKasOffline(): boolean {
  return kasOffline;
}

/** One lightweight reachability probe. Resolves true iff the KAS answered 200 to the public
 *  key GET within the bounded timeout. Any network error / non-2xx / timeout ⇒ false (a KAS
 *  process down surfaces as a caddy 5xx; the gateway down surfaces as a network error). */
async function probeKas(): Promise<boolean> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), KAS_PROBE_TIMEOUT_MS);
  try {
    const res = await fetch(`${window.location.origin}/kas/v2/kas_public_key`, {
      method: "GET",
      signal: controller.signal,
      cache: "no-store",
    });
    return res.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/** Start the KAS reachability heartbeat. Probes IMMEDIATELY (so the state is fresh at boot),
 *  then every KAS_HEARTBEAT_MS. Applies the thresholded flip and invokes `onChange(offline)`
 *  ONLY on a transition (edge-triggered) so the host recomputes just when the verdict actually
 *  changes. Returns a stop function — call it on dispose. */
export function startKasHeartbeat(onChange: (offline: boolean) => void): () => void {
  let stopped = false;
  const tick = async (): Promise<void> => {
    if (stopped) return;
    const ok = await probeKas();
    if (stopped) return;
    if (ok) {
      kasProbeFailures = 0;
      if (kasOffline) {
        kasOffline = false;
        onChange(false); // recovered — flip back on the FIRST success
      }
    } else {
      kasProbeFailures++;
      if (!kasOffline && kasProbeFailures >= KAS_OFFLINE_THRESHOLD) {
        kasOffline = true;
        onChange(true); // N consecutive failures — declare offline (fail closed)
      }
    }
  };
  void tick(); // immediate first probe
  const interval = window.setInterval(() => void tick(), KAS_HEARTBEAT_MS);
  return () => {
    stopped = true;
    window.clearInterval(interval);
  };
}

/** Build the client-side KAS client for the logged-in persona. Same-origin via the
 *  gateway (no mixed-content): platform = page origin, KAS = origin + /kas. */
export function initKas(initialToken: string): void {
  token = initialToken;
  tokenExpiresAt = jwtExpiryMs(initialToken);
  const origin = window.location.origin;
  const kasUrl = `${origin}/kas`;
  client = new OpenTDF({
    platformUrl: origin,
    // The interceptor is the single seam every KAS request (rewrap AND seal) flows through, so
    // making the token-getter refresh-aware covers all paths at once: refresh proactively just
    // before expiry, then return the live token.
    interceptors: [authTokenInterceptor(async () => {
      await ensureFreshToken(false);
      return token;
    })],
    // Read (I5 release) and PRODUCE (I5 bind, the chat seal) share ONE client, ONE
    // same-origin /kas, ONE live JWT via the interceptor — so a persona switch via
    // setKasToken re-targets the produce subject exactly as it re-targets the read
    // subject. The KAS bakes this same-origin endpoint into every sealed TDF, so the
    // reader opens it same-origin too (no mixed-content, no second URL to drift).
    defaultReadOptions: { allowedKASEndpoints: [kasUrl] },
    defaultCreateOptions: { defaultKASEndpoint: kasUrl },
    disableDPoP: true,
  });
}

/** Zeroize the module-held rewrap token and drop the cached client (closes the
 *  logout-token-not-zeroized gap). After this, decryptTdf/sealTdf return/throw
 *  "KAS client not initialized" until the next initKas — no stale persona token
 *  lingers for an interceptor to replay. Called on logout. */
export function clearKasToken(): void {
  token = "";
  tokenExpiresAt = 0;
  tokenGeneration++; // invalidate any in-flight refresh so it can't land post-logout
  refreshInFlight = null;
  client = null;
}

/** Identity-switch listeners (e.g. the chat plaintext cache). Cleared/notified on every
 *  setKasToken so no persona inherits a lower/higher persona's recovered bodies. */
const identitySwitchListeners = new Set<() => void>();

/** Register a callback fired on EVERY persona switch (setKasToken). The chat key-access
 *  adapter registers its plaintext-cache clear here so a new (lower) persona re-decides
 *  every ref — fail-closed identity-switch discipline, structural not remembered. */
export function onIdentitySwitch(fn: () => void): () => void {
  identitySwitchListeners.add(fn);
  return () => identitySwitchListeners.delete(fn);
}

/** Swap the rewrap+seal identity to the newly-logged-in persona (called on persona
 *  switch); the interceptor reads this live, so subsequent opens AND seals rewrap/bind
 *  AS the new identity. Fires identity-switch listeners so caches that hold a prior
 *  persona's released plaintext are cleared (fail-closed). */
export function setKasToken(next: string): void {
  token = next;
  tokenExpiresAt = jwtExpiryMs(next);
  // Bump the generation so any refresh started for the PRIOR persona is discarded on completion
  // (ensureFreshToken checks gen) — a persona switch must never inherit the old identity's token.
  tokenGeneration++;
  // FIX 6 — ROBUST LISTENER LOOP. Each listener runs in its own try/catch so a throwing
  // PEER listener can NEVER abort the loop and skip a later listener's cache clear (that
  // would fail OPEN — a persona switch that silently retained the prior persona's opened
  // bodies). A bad peer is logged and the loop continues.
  for (const fn of identitySwitchListeners) {
    try {
      fn();
    } catch (e) {
      console.error("[kas] identity-switch listener threw (continuing — cache clears must not be skipped):", e);
    }
  }
}

/** Pull the inner payload's self-declared marking from the decrypted plaintext. The
 *  front gateway seals a JSON `{ marking, fields }` (CONTRACT:P4-COT-TDF-WRAPPER); a
 *  cleared reader must see (and the COP must mark the screen to) that HIGHER inner
 *  classification — not the releasable outer floor that rode the wire. */
function parseInnerMarking(plaintext: string): InnerMarking | undefined {
  const t = plaintext.trim();
  if (!t.startsWith("{")) return undefined;
  try {
    const o = JSON.parse(t) as { marking?: InnerMarking };
    const m = o?.marking;
    if (m && typeof m === "object" && typeof m.classification === "string") return m;
  } catch {
    /* opaque / non-structured payload — no inner marking to surface */
  }
  return undefined;
}

/** Ask the live KAS to rewrap + decrypt a protected TDF AS the current persona. The
 *  ref is either a same-origin URL (seeded objects) or an inline `data:tdf;base64,…`
 *  payload (live gateway-protected CoT). Fails closed (denied) on any error — the KAS
 *  is the only thing that releases a key. */
export async function decryptTdf(ref: string): Promise<RewrapOutcome> {
  if (!client) return { granted: false, reason: "KAS client not initialized" };
  // FAIL CLOSED WHEN THE KAS IS UNREACHABLE (Phase 2 Step E). The heartbeat has seen N
  // consecutive failed probes → SKIP the rewrap attempt entirely: no network round-trip and
  // no per-item error cascade. The object stays SEALED at its outer marking (never plaintext).
  // Recovers automatically the moment the heartbeat flips back online. This is the single
  // decrypt chokepoint (onSelect + chatKeyAccess both route here), so both fail closed for free.
  if (kasOffline) return { granted: false, reason: "KAS_OFFLINE" };
  const bytes = ref.startsWith("data:")
    ? Uint8Array.from(atob(ref.slice(ref.indexOf(",") + 1)), (c) => c.charCodeAt(0))
    : new Uint8Array(await (await fetch(ref)).arrayBuffer());
  const openAndRead = async (): Promise<RewrapOutcome> => {
    const reader = await client!.open({ source: { type: "buffer", location: bytes } });
    const out = await reader.decrypt();
    const plaintext = await new Response(out).text();
    return { granted: true, plaintext, marking: parseInnerMarking(plaintext) };
  };
  try {
    return await openAndRead();
  } catch (e) {
    const reason = classifyKasError(e);
    // REACTIVE BACKSTOP: a 401 that the proactive refresh didn't pre-empt (e.g. clock skew, or a
    // token that expired between the interceptor check and the KAS reaching it) — force ONE
    // refresh and retry ONCE. If the retry still fails, fall closed with the real reason. This
    // never loosens the gate: a genuine ACCESS_DENIED is not UNAUTHENTICATED, so it never retries.
    if (reason === "UNAUTHENTICATED" && refresher) {
      const before = token;
      await ensureFreshToken(true);
      if (token !== before) {
        try {
          return await openAndRead();
        } catch (e2) {
          return { granted: false, reason: classifyKasError(e2) };
        }
      }
    }
    return { granted: false, reason };
  }
}

/** SEAL a cleartext body as a REAL TDF, client-side, AS the logged-in persona (the
 *  produce side of CONTRACT:I5-KEY-ACCESS.1.0). Mirrors the gateway's protect.ts:94 but
 *  the subject is the OPERATOR, not a service identity (CHAT-BUILD-SPEC §2.1, L1).
 *
 *  LOCKSTEP (the whole point): the marking VALUES are routed to bound FQNs via the SAME
 *  deriveBoundFqns the I4 visibility filter uses (against the SAME live policy bundle),
 *  so the I5 produce-bind and the I4 read-filter decide over IDENTICAL attributes. A
 *  free-text mission/room value that binds no attribute is dropped (isolation tag, not a
 *  gating attribute — CHAT-BUILD-SPEC §3 bundle-binding caveat).
 *
 *  FAIL-CLOSED ASYMMETRY (CHAT-BUILD-SPEC §2.1): unlike protect.ts (which fails OPEN —
 *  returns null so the gateway never drops a track), this REJECTS by THROWING on any
 *  failure. The caller must keep the draft and surface an error — never silently send a
 *  clear body in a room whose floor is above UNCLASSIFIED. Returns a `data:tdf;base64,…`
 *  ref in EXACTLY the format decryptTdf already consumes (the read/produce round-trip). */
export async function sealTdf(
  text: string,
  labels: ProtectLabels,
  bundle: PolicyBundle,
  expectedNamespace?: string,
): Promise<string> {
  if (!client) throw new Error("KAS client not initialized");

  // FIX 3 — BUNDLE / NAMESPACE LOCKSTEP. The bundle this seal binds against MUST be the
  // SAME live bundle the I4 filter + KAS decide over. A seed/stale bundle (tdfbot.local,
  // classification_level) binds a DIFFERENT attribute universe than live (defense.local,
  // classification) → the KAS would release per attributes the reader's PDP never saw.
  // Defensively refuse a degenerate/wrong-namespace bundle BEFORE binding anything.
  if (!bundle || !Array.isArray(bundle.attributes) || bundle.attributes.length === 0) {
    throw new Error("UNBINDABLE_MARKING: empty/degenerate policy bundle — refusing to seal");
  }

  // R3 — EXPECTED-NAMESPACE MANDATORY / FAIL-CLOSED. Binding against a bundle whose
  // namespace we have NOT verified against the live config means the I5 produce-bind and
  // the I4 read-filter could decide over DIFFERENT attribute universes. For a RESTRICTIVE
  // marking that asymmetry is a release-to-the-wrong-reader bug, so we refuse to seal
  // without an explicit expectedNamespace to pin the bundle. (An unrestricted UNCLASSIFIED
  // / release-to-everyone body carries no audience secret, so it may seal without one.)
  const restrictive = isRestrictiveMarking(labels);
  if (!expectedNamespace && restrictive) {
    throw new Error(
      "MISSING_EXPECTED_NAMESPACE: refusing to seal a restrictive marking without a verified live namespace (R3 fail-closed)",
    );
  }
  if (expectedNamespace && bundle.namespace !== expectedNamespace) {
    throw new Error(
      `BUNDLE_MISMATCH: seal bundle namespace "${bundle.namespace}" != live "${expectedNamespace}" (lockstep)`,
    );
  }

  const security: SecurityView = {
    classification: labels.classification,
    releasability: labels.releasability,
    caveats: labels.caveats,
    mission: labels.mission,
  };

  // FIX 4 — VALIDATE MARKINGS BEFORE SEAL. The I4 read path fails closed on internally
  // contradictory markings (e.g. NOFORN + REL_TO GBR); the seal path must too, or a
  // contradictory body could be sealed + KAS-granted while I4 would hide it. Same
  // validator the read path uses (validateMarkings → decide.ts).
  const validation = validateMarkings(security, bundle);
  if (!validation.valid) {
    const reasons = validation.violations.filter((v) => v.severity === "error").map((v) => v.rule).join(",");
    throw new Error(`CONTRADICTORY_MARKING: refusing to seal (${reasons || "invalid"})`);
  }

  // Route marking VALUES → bound FQNs through the identical PDP path (lockstep). The
  // SecurityView shape is the labels themselves; deriveBoundFqns normalizes + binds.
  const attributes = deriveBoundFqns(security, bundle);

  // FIX 2 + FIX 5 — UNBOUND-SEAL FAIL-OPEN + CLASSIFICATION FLOOR. createTDF([]) would
  // produce an encrypted-but-UNBOUND TDF the KAS releases to ANY authenticated subject.
  // For any RESTRICTIVE facet (classification > UNCLASSIFIED, any caveat, or a
  // releasability narrower than "all"), the corresponding attribute MUST actually bind —
  // else we refuse. The hierarchy (classification floor) facet is asserted explicitly so
  // a SECRET body whose value didn't resolve to an FQN can NEVER seal unbound.
  const cls = (labels.classification ?? "UNCLASSIFIED").toUpperCase();
  const clsRank = CLASS_RANK[cls] ?? Number.POSITIVE_INFINITY; // unknown class → treat as restrictive
  const hierAttr = bundle.attributes.find((a) => a.rule === "hierarchy");
  const hierName = hierAttr?.name ?? "";
  // Did the classification floor actually bind to the hierarchy attribute's FQN?
  const floorBound = !!hierName && attributes.some((f) => f.includes(`/attr/${hierName}/value/`));
  const hasCaveats = (labels.caveats ?? []).filter(Boolean).length > 0;
  // The CLASSIFICATION-FLOOR restrictiveness gates the unbound-floor / empty-attrs checks
  // (an above-UNCLASSIFIED or caveated body MUST bind its hierarchy floor). This is NARROWER
  // than the R3 `restrictive` (which also fires on audience-narrowed releasability): a
  // UNCLASSIFIED//REL-NATO body is R3-restrictive (needs a namespace) but its UNCLASSIFIED
  // floor legitimately binds no hierarchy FQN, so it must NOT trip the floor gate.
  const floorRestrictive = clsRank > CLASS_RANK.UNCLASSIFIED || hasCaveats;

  if (floorRestrictive && !floorBound) {
    // The body is restricted but its classification floor bound NO attribute → an unbound
    // TDF would fail OPEN. Fail CLOSED instead. (An unrestricted UNCLASSIFIED/REL-to-all
    // body is the only case allowed to bind few/zero attributes.)
    throw new Error(
      `UNBINDABLE_MARKING: classification "${cls}" did not bind the floor attribute "${hierName || "<none>"}" — refusing to seal an unbound restricted body`,
    );
  }
  if (floorRestrictive && attributes.length === 0) {
    throw new Error(`UNBINDABLE_MARKING: restricted body bound NO attributes — refusing to seal unbound`);
  }

  const cipher = await client.createTDF({
    source: { type: "buffer", location: new TextEncoder().encode(text) },
    attributes,
    autoconfigure: false,
  });
  const bytes = new Uint8Array(await new Response(cipher).arrayBuffer());
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return `data:tdf;base64,${btoa(bin)}`;
}

/** Load the seeded protected objects (real defense.local TDFs minted by
 *  scripts/seed-cop-tdf.mjs). Empty if not seeded — the COP degrades gracefully. */
export async function loadSeededObjects(): Promise<SeededObject[]> {
  try {
    const r = await fetch("/tdf/index.json");
    if (!r.ok) return [];
    const idx = (await r.json()) as { objects?: SeededObject[] };
    return (idx.objects ?? []).map((o) => ({ ...o, tdf: o.tdf.startsWith("/") ? o.tdf : `/${o.tdf}` }));
  } catch {
    return [];
  }
}

/** Map an SDK error to a compact I5 reason code for the HUD. */
function classifyKasError(e: unknown): string {
  const m = e instanceof Error ? e.message : String(e);
  if (/403|permission denied/i.test(m)) return "ACCESS_DENIED";
  if (/401|auth/i.test(m)) return "UNAUTHENTICATED";
  if (/network|failed to fetch|404|unreachable|unimplemented/i.test(m)) return "KAS_UNAVAILABLE";
  return m.slice(0, 80);
}
